import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Home, Calendar, Bell } from 'lucide-react';
import { supabase } from '../supabase/client';
import { formatDate } from '../utils/timeUtils';

interface Notice {
  id: number;
  title: string;
  content: string;
  summary: string;
  publish_time: string;
  is_pinned: boolean;
  is_highlighted: boolean;
  category: string;
}

export default function NoticeDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [notice, setNotice] = useState<Notice | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotice();
  }, [id]);

  const fetchNotice = async () => {
    if (!id) return;
    const { data } = await supabase
      .from('homepage_notices')
      .select('*')
      .eq('id', parseInt(id, 10))
      .single();
    if (data) setNotice(data as Notice);
    setLoading(false);
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!notice) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <p className="text-gray-500 mb-4">通知不存在或已被删除</p>
        <Link to="/notices" className="text-blue-600 hover:underline">返回通知列表</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>返回</span>
            </button>
            <div className="h-6 w-px bg-gray-200" />
            <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors">
              <Home className="w-5 h-5" />
              <span>首页</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
        >
          <div className="p-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                {notice.category || '通知'}
              </span>
              {notice.is_pinned && (
                <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium flex items-center gap-1">
                  <Bell className="w-3 h-3" />置顶
                </span>
              )}
            </div>

            <h1 className={`text-2xl font-bold mb-4 ${notice.is_highlighted ? 'text-red-600' : 'text-gray-900'}`}>
              {notice.title}
            </h1>

            <div className="flex items-center gap-2 text-gray-500 text-sm mb-8 pb-8 border-b border-gray-100">
              <Calendar className="w-4 h-4" />
              <span>发布时间：{formatDate(notice.publish_time)}</span>
            </div>

            <div
              className="prose prose-blue max-w-none"
              dangerouslySetInnerHTML={{ __html: notice.content || notice.summary || '暂无内容' }}
            />
          </div>
        </motion.div>
      </main>
    </div>
  );
}
